/**
 * TanStack Query hooks over the typed action-plans API client.
 *
 * Reads use `useQuery`; writes use `useMutation` and invalidate the affected
 * subtree. Inline autosave editors don't call these mutation hooks — they wire
 * `useAutosave.save` straight to `patchAction` so the debounce/abort/beacon
 * orchestration owns the request lifecycle (see ActionEditor).
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";

import { toast } from "sonner";

import { api, refreshAccessToken } from "@/lib/api/client";
import { API_ORIGIN } from "@/lib/config";
import { getAccessToken } from "@/lib/auth/tokens";
import { getOnBehalfOf } from "@/lib/api/on-behalf";
import i18n from "@/lib/i18n/i18n";
import { unwrap } from "@/lib/api/unwrap";
import type { HistoryResponse } from "@/components/HistoryDrawer";
import { qKeys } from "@/features/questionnaire/api";

import { apiErrorDetail } from "../lib/errors";
import { patchPlanActions, patchPlanActionsBy } from "../lib/selection";
import { runInWaves } from "../lib/waves";
import { actionPlanKeys } from "./keys";
import type {
  ActionPatch,
  ActionPlanAction,
  ActionPlanConsultation,
  ActionPlanCreate,
  ActionPlanDetail,
  ActionPlanGoalResponse,
  ActionPlanListItem,
  PrioritizationData,
  StakeholderGroup,
} from "./types";

// --- raw request helpers (also used directly by autosave) --------------------

/** PATCH an action. `signal` lets useAutosave abort a superseded write. */
export function patchAction(
  id: string,
  body: ActionPatch,
  signal?: AbortSignal,
): Promise<ActionPlanAction> {
  return unwrap(
    api.PATCH("/api/v1/action-plans/actions/{id}/", {
      params: { path: { id } },
      body,
      signal,
    }),
  );
}

/** PATCH a goal (reword the objective name). Signal-aware, like patchAction.
 * Rewording upserts a per-user GoalCustomization server-side; only custom_name
 * is meaningful. */
export function patchGoal(
  id: string,
  // ``action_plan`` names the plan the rename is recorded against in its
  // history; without it the API saves the rename and logs nothing.
  body: { custom_name?: string; action_plan?: string },
  signal?: AbortSignal,
): Promise<ActionPlanGoalResponse> {
  return unwrap(
    api.PATCH("/api/v1/action-plans/goals/{id}/", {
      params: { path: { id } },
      body: body as never,
      signal,
    }),
  );
}

/**
 * Create a custom (extra) objective under an issue, together with its first
 * course of action.
 *
 * A custom objective must be born with at least one action: the plan hierarchy
 * materializes goals from their `ActionPlanAction`s, so a goal with no action
 * would never render and could never be garbage-collected. The plan id and the
 * first action name are therefore sent alongside the objective name.
 */
export function createGoal(
  planId: string,
  issueId: string,
  name: string,
  actionName: string,
): Promise<ActionPlanGoalResponse> {
  return unwrap(
    api.POST("/api/v1/action-plans/goals/", {
      body: {
        action_plan: planId,
        action_plan_issue: issueId,
        name,
        action_name: actionName,
      } as never,
    }),
  );
}

/**
 * Delete a custom (extra) objective from ONE plan.
 *
 * Custom goals/actions are shared catalogue rows reused across the member's
 * plans, so the plan id must be sent: the backend removes only this plan's rows
 * for the goal's custom actions and reference-counts the catalogue row, instead
 * of cascading it out of every plan.
 */
export function deleteGoal(id: string, planId: string): Promise<unknown> {
  return unwrap(
    api.DELETE("/api/v1/action-plans/goals/{id}/", {
      params: { path: { id }, query: { action_plan: planId } },
    }),
  );
}

/**
 * Create a custom (extra) course of action under a goal.
 *
 * Custom actions are now user-scoped catalogue `Action` rows, so the catalogue
 * goal no longer implies a plan — the plan id must be sent explicitly.
 */
export function createAction(
  planId: string,
  goalId: string,
  name: string,
): Promise<ActionPlanAction> {
  return unwrap(
    api.POST("/api/v1/action-plans/actions/", {
      body: { action_plan: planId, action_plan_goal: goalId, name } as never,
    }),
  );
}

/** Delete a custom (extra) course of action. */
export function deleteAction(id: string): Promise<unknown> {
  return unwrap(
    api.DELETE("/api/v1/action-plans/actions/{id}/", { params: { path: { id } } }),
  );
}

/** Mark the Step-2 guided tour as seen for the current member. */
export function dismissActionsTour(planId: string): Promise<unknown> {
  return unwrap(
    api.POST("/api/v1/action-plans/{id}/dismiss-actions-tour/", {
      params: { path: { id: planId } },
    }),
  );
}

/** Mark the Step-3 guided tour as seen for the current member. */
export function dismissPrioritizationTour(planId: string): Promise<unknown> {
  return unwrap(
    api.POST("/api/v1/action-plans/{id}/dismiss-prioritization-tour/", {
      params: { path: { id: planId } },
    }),
  );
}

/** PATCH a plan. Signal-aware sibling of patchAction for autosave forms. */
export function patchPlan(
  id: string,
  body: Partial<ActionPlanDetail>,
  signal?: AbortSignal,
): Promise<ActionPlanDetail> {
  return unwrap(
    api.PATCH("/api/v1/action-plans/{id}/", {
      params: { path: { id } },
      body,
      signal,
    }),
  );
}

// --- action plans ------------------------------------------------------------

export function useActionPlans(options?: {
  enabled?: boolean;
}): UseQueryResult<ActionPlanListItem[]> {
  return useQuery({
    queryKey: actionPlanKeys.lists(),
    enabled: options?.enabled,
    queryFn: async () => {
      const page = await unwrap(api.GET("/api/v1/action-plans/", {}));
      return page.results;
    },
  });
}

export function useActionPlan(
  id: string | null,
): UseQueryResult<ActionPlanDetail> {
  return useQuery({
    queryKey: actionPlanKeys.detail(id ?? "none"),
    enabled: id != null,
    queryFn: () =>
      unwrap(
        api.GET("/api/v1/action-plans/{id}/", {
          params: { path: { id: id as string } },
        }),
      ),
  });
}

export function useCreateActionPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name?: string;
      time_planned_startyear?: number | null;
      time_planned_endyear?: number | null;
      // `id` is read-only on the server but drf-spectacular marks it required on
      // the shared create component; cast so callers don't send a fake id.
    }) => unwrap(api.POST("/api/v1/action-plans/", { body: body as ActionPlanCreate })),
    onSuccess: () => qc.invalidateQueries({ queryKey: actionPlanKeys.lists() }),
  });
}

export function useUpdateActionPlan(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<ActionPlanDetail>) =>
      unwrap(
        api.PATCH("/api/v1/action-plans/{id}/", {
          params: { path: { id } },
          body,
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: actionPlanKeys.detail(id) });
      void qc.invalidateQueries({ queryKey: actionPlanKeys.lists() });
    },
  });
}

export function useDeleteActionPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(
        api.DELETE("/api/v1/action-plans/{id}/", {
          params: { path: { id } },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: actionPlanKeys.lists() }),
  });
}

/**
 * How many action PATCHes are in flight at once. The API writes one action per
 * request, and a bulk toggle on the selection step can cover every action in the
 * plan (the questionnaire catalogue reaches ~300), so the requests go out in
 * bounded waves rather than all at once — browsers queue past ~6 per host anyway,
 * and a single burst that large reads as a stalled UI.
 */
const PATCH_CONCURRENCY = 6;

async function patchActionsInWaves(
  updates: { id: string; body: ActionPatch }[],
): Promise<void> {
  return runInWaves(updates, PATCH_CONCURRENCY, (u) => patchAction(u.id, u.body));
}

/**
 * PATCH one or more actions, then refresh the owning plan (and the list, whose
 * selected/completed counts derive from actions). Used by discrete toggles
 * (selection, status, priority order) — not the debounced text autosave.
 *
 * A bulk toggle (e.g. including an issue patches every action beneath it) can
 * fail partway: some writes commit, one rejects. We re-sync from the server on
 * *any* outcome (onSettled) so the checkboxes reflect what actually persisted
 * rather than an optimistic guess, and surface the failure via a toast instead
 * of swallowing it.
 */
export function useUpdateActions(planId: string) {
  const qc = useQueryClient();
  const key = actionPlanKeys.detail(planId);
  return useMutation({
    mutationKey: key,
    mutationFn: (updates: { id: string; body: ActionPatch }[]) =>
      patchActionsInWaves(updates),
    // Everything on screen is derived from the cached plan — the group counts
    // in a step's summary, the objective's shared-set picker, the track step's
    // progress — so applying the write here is what makes those move on click
    // instead of a round trip later.
    onMutate: async (updates) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<ActionPlanDetail>(key);
      if (previous) {
        const next = updates.reduce(
          (plan, update) => patchPlanActions(plan, [update.id], update.body),
          previous,
        );
        qc.setQueryData(key, next);
      }
      return { previous };
    },
    onError: (err, _variables, context) => {
      if (context?.previous) qc.setQueryData(key, context.previous);
      toast.error(apiErrorDetail(err) ?? i18n.t("actionPlans.errors.saveFailed"));
    },
    // Still re-sync from the server on any outcome: a batch can fail partway,
    // and the server may derive more than we patched here — assigning a group
    // mints or removes that group's consultation, for one.
    onSettled: () => {
      if (qc.isMutating({ mutationKey: key }) > 1) return;
      invalidatePlanWrites(qc, planId);
    },
  });
}

// --- stakeholder groups ------------------------------------------------------

export function useStakeholderGroups(): UseQueryResult<StakeholderGroup[]> {
  return useQuery({
    queryKey: actionPlanKeys.groups(),
    queryFn: async () => {
      const page = await unwrap(
        api.GET("/api/v1/action-plans/stakeholder-groups/", {}),
      );
      return page.results;
    },
  });
}

/** PATCH a stakeholder group. Signal-aware, for the group autosave form. */
export function patchStakeholderGroup(
  id: string,
  body: Partial<StakeholderGroup>,
  signal?: AbortSignal,
): Promise<StakeholderGroup> {
  return unwrap(
    api.PATCH("/api/v1/action-plans/stakeholder-groups/{id}/", {
      params: { path: { id } },
      body,
      signal,
    }),
  );
}

export function useCreateStakeholderGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; intro_message?: string }) =>
      unwrap(
        api.POST("/api/v1/action-plans/stakeholder-groups/", {
          body: body as StakeholderGroup,
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: actionPlanKeys.groups() }),
  });
}

export function useDeleteStakeholderGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(
        api.DELETE("/api/v1/action-plans/stakeholder-groups/{id}/", {
          params: { path: { id } },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: actionPlanKeys.groups() }),
  });
}

// --- consultations -----------------------------------------------------------

export function useConsultations(
  planId: string,
): UseQueryResult<ActionPlanConsultation[]> {
  return useQuery({
    queryKey: actionPlanKeys.consultations(planId),
    queryFn: async () => {
      const page = await unwrap(
        api.GET("/api/v1/action-plans/consultations/", {}),
      );
      return page.results.filter((c) => c.action_plan === planId);
    },
  });
}

export function useCreateConsultation(planId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { action_plan: string; stakeholder_group: string }) =>
      unwrap(
        api.POST("/api/v1/action-plans/consultations/", {
          body: body as ActionPlanConsultation,
        }),
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: actionPlanKeys.consultations(planId) }),
  });
}

export function useDeleteConsultation(planId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(
        api.DELETE("/api/v1/action-plans/consultations/{id}/", {
          params: { path: { id } },
        }),
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: actionPlanKeys.consultations(planId) }),
  });
}

// --- prioritization ----------------------------------------------------------

/** Computed prioritization data (groups + per-action scores). The endpoint is a
 *  custom action returning aggregates, so its response is loosely typed by the
 *  generated client — cast to the hand-declared PrioritizationData. */
export function usePrioritization(planId: string): UseQueryResult<PrioritizationData> {
  return useQuery({
    queryKey: actionPlanKeys.prioritization(planId),
    queryFn: () =>
      unwrap(
        api.GET("/api/v1/action-plans/{id}/prioritization/", {
          params: { path: { id: planId } },
        }),
        // The endpoint is a custom action; the generated client defaults its
        // response to the plan-detail shape, so cast to the real payload.
      ) as unknown as Promise<PrioritizationData>,
  });
}

/** Refetch prioritization scores + the plan detail after a change that moves them. */
interface PlanCacheContext {
  previous?: ActionPlanDetail;
}

/**
 * Optimistic writes against the cached plan.
 *
 * Every workspace step derives what it shows — ticks, tallies, ordering, the
 * side navs — from that one cached object, so a mutation that merely
 * invalidates leaves the control the user just operated showing its old value
 * until the refetch lands. Apply the change up front and roll it back if the
 * request fails; the invalidation still runs afterwards, so the server stays
 * the final word.
 */
function planCache(qc: ReturnType<typeof useQueryClient>, planId: string) {
  const key = actionPlanKeys.detail(planId);
  return {
    key,
    async apply(
      update: (plan: ActionPlanDetail) => ActionPlanDetail,
    ): Promise<PlanCacheContext> {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<ActionPlanDetail>(key);
      if (previous) qc.setQueryData(key, update(previous));
      return { previous };
    },
    rollback(context: PlanCacheContext | undefined) {
      if (context?.previous) qc.setQueryData(key, context.previous);
    },
  };
}

/**
 * Everything a write to this plan's actions can affect.
 *
 * Selection and per-action writes share a mutation key, and each defers to the
 * other while one is still in flight (see the isMutating gate) — so they must
 * invalidate the *same* set, or the one that ends up running the sweep leaves
 * behind whatever only the other one knew to refresh. The server also derives
 * work from these writes: consultation rows are reconciled on any group or
 * selection change, and step 3's scores are computed from both.
 */
function invalidatePlanWrites(
  qc: ReturnType<typeof useQueryClient>,
  planId: string,
) {
  void qc.invalidateQueries({ queryKey: actionPlanKeys.detail(planId) });
  void qc.invalidateQueries({ queryKey: actionPlanKeys.lists() });
  void qc.invalidateQueries({ queryKey: actionPlanKeys.consultations(planId) });
  void qc.invalidateQueries({ queryKey: actionPlanKeys.prioritization(planId) });
}

function invalidatePrioritization(qc: ReturnType<typeof useQueryClient>, planId: string) {
  void qc.invalidateQueries({ queryKey: actionPlanKeys.prioritization(planId) });
  void qc.invalidateQueries({ queryKey: actionPlanKeys.detail(planId) });
}

export function useReorderActions(planId: string) {
  const qc = useQueryClient();
  const cache = planCache(qc, planId);
  return useMutation({
    mutationFn: (order: string[]) =>
      unwrap(
        api.POST("/api/v1/action-plans/{id}/reorder/", {
          params: { path: { id: planId } },
          body: { order } as never,
        }),
      ),
    // The table is sorted by priority_order off the cached plan, so without
    // this the rows keep their old order until the refetch — "Auto-sort by
    // score" looked like it had done nothing.
    onMutate: (order) => {
      const index = new Map(order.map((id, position) => [id, position]));
      return cache.apply((plan) =>
        patchPlanActionsBy(plan, (action) =>
          index.has(action.id)
            ? { priority_order: index.get(action.id) }
            : undefined,
        ),
      );
    },
    onSuccess: () => invalidatePrioritization(qc, planId),
    onError: (err, _order, context) => {
      cache.rollback(context);
      toast.error(apiErrorDetail(err) ?? i18n.t("actionPlans.errors.saveFailed"));
    },
  });
}

/**
 * Select or deselect a set of actions in one request, applied to the cache
 * immediately.
 *
 * The per-action PATCH is right for editing one row, but a bulk toggle covers
 * every action under an issue or objective — 84 of them for the largest issue
 * in the catalogue — and each PATCH re-runs the plan-wide consultation sync
 * server-side. Ticking one issue took ~2.5s and dozens of requests that way.
 *
 * The write is optimistic: the tick, the counts and the coverage bars all read
 * from the cached plan, so without it a click sat there doing nothing until the
 * round trip finished. A failure rolls the cache back and says so.
 */
export function useSetActionSelection(planId: string) {
  const qc = useQueryClient();
  const key = actionPlanKeys.detail(planId);
  return useMutation({
    mutationKey: key,
    // One selection write at a time per plan. The controls stay live (that is
    // the point of the optimistic tick), so a quick tick–untick on the same
    // issue puts two writes in flight for the same ids; served concurrently
    // they can commit in either order and the loser silently wins, leaving the
    // opposite of the last click. Queued, the last click is the last write.
    scope: { id: `plan-selection:${planId}` },
    mutationFn: ({
      actionIds,
      isSelected,
    }: {
      actionIds: string[];
      isSelected: boolean;
    }) =>
      unwrap(
        api.POST("/api/v1/action-plans/{id}/selection/", {
          params: { path: { id: planId } },
          body: { action_ids: actionIds, is_selected: isSelected },
        }),
      ),
    onMutate: async ({ actionIds, isSelected }) => {
      // Stop an in-flight refetch from landing on top of the optimistic tree.
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<ActionPlanDetail>(key);
      if (previous) {
        qc.setQueryData(
          key,
          patchPlanActions(previous, actionIds, { is_selected: isSelected }),
        );
      }
      return { previous };
    },
    onError: (err, _variables, context) => {
      if (context?.previous) qc.setQueryData(key, context.previous);
      toast.error(apiErrorDetail(err) ?? i18n.t("actionPlans.errors.saveFailed"));
    },
    // Re-sync from the server once the last toggle settles — refetching while
    // another is still in flight would flash that one's row back to its old
    // state before its own response lands.
    onSettled: () => {
      if (qc.isMutating({ mutationKey: key }) > 1) return;
      invalidatePlanWrites(qc, planId);
    },
  });
}

export function useUnselectActions(planId: string) {
  const qc = useQueryClient();
  const cache = planCache(qc, planId);
  return useMutation({
    mutationFn: (actionIds: string[]) =>
      unwrap(
        api.POST("/api/v1/action-plans/{id}/unselect/", {
          params: { path: { id: planId } },
          body: { action_ids: actionIds } as never,
        }),
      ),
    onMutate: (actionIds) =>
      cache.apply((plan) =>
        patchPlanActions(plan, actionIds, { is_selected: false }),
      ),
    onSuccess: () => {
      invalidatePrioritization(qc, planId);
      void qc.invalidateQueries({ queryKey: actionPlanKeys.lists() });
      void qc.invalidateQueries({ queryKey: actionPlanKeys.consultations(planId) });
    },
    onError: (err, _actionIds, context) => {
      cache.rollback(context);
      toast.error(apiErrorDetail(err) ?? i18n.t("actionPlans.errors.saveFailed"));
    },
  });
}

export function useSetPrePrioritization(planId: string) {
  const qc = useQueryClient();
  const key = actionPlanKeys.prioritization(planId);
  return useMutation({
    mutationFn: (vars: { actionId: string; priority: number | null }) =>
      unwrap(
        api.POST("/api/v1/action-plans/actions/{id}/pre-prioritization/", {
          params: { path: { id: vars.actionId } },
          body: { priority: vars.priority } as never,
        }),
      ),
    // The select reads its value from this query, so leaving it to the refetch
    // made the dropdown snap back to the old number for a moment. The score
    // beside it is a server computation and stays as it was until the refetch.
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<PrioritizationData>(key);
      if (previous) {
        qc.setQueryData<PrioritizationData>(key, {
          ...previous,
          actions: previous.actions.map((a) =>
            a.action_id === vars.actionId ? { ...a, pre_prio: vars.priority } : a,
          ),
        });
      }
      return { previous };
    },
    // A questionnaire action's rating is the self-assessment's importance,
    // shared by every plan of the member: the questionnaire's answers and the
    // other plans read it too.
    onSuccess: () => {
      invalidatePrioritization(qc, planId);
      void qc.invalidateQueries({ queryKey: actionPlanKeys.all });
      void qc.invalidateQueries({ queryKey: qKeys.allAnswers() });
    },
    onError: (err, _vars, context) => {
      if (context?.previous) qc.setQueryData(key, context.previous);
      toast.error(apiErrorDetail(err) ?? i18n.t("actionPlans.errors.saveFailed"));
    },
  });
}

export function useToggleConsultation(planId: string) {
  const qc = useQueryClient();
  const listKey = actionPlanKeys.consultations(planId);
  const prioKey = actionPlanKeys.prioritization(planId);
  return useMutation({
    mutationFn: (vars: { id: string; is_open: boolean }) =>
      unwrap(
        api.PATCH("/api/v1/action-plans/consultations/{id}/", {
          params: { path: { id: vars.id } },
          body: { is_open: vars.is_open } as Partial<ActionPlanConsultation>,
        }),
      ),
    // Open/closed is shown in two places — the consultation list and the
    // step-3 group header — and both read from cache, so the toggle sat on its
    // old position until the round trip finished.
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: listKey });
      await qc.cancelQueries({ queryKey: prioKey });
      const previousList =
        qc.getQueryData<ActionPlanConsultation[]>(listKey);
      const previousPrio = qc.getQueryData<PrioritizationData>(prioKey);
      if (previousList) {
        qc.setQueryData<ActionPlanConsultation[]>(
          listKey,
          previousList.map((c) =>
            c.id === vars.id ? { ...c, is_open: vars.is_open } : c,
          ),
        );
      }
      if (previousPrio) {
        qc.setQueryData<PrioritizationData>(prioKey, {
          ...previousPrio,
          groups: previousPrio.groups.map((g) =>
            g.consultation_id === vars.id ? { ...g, is_open: vars.is_open } : g,
          ),
        });
      }
      return { previousList, previousPrio };
    },
    onSuccess: () => {
      invalidatePrioritization(qc, planId);
      void qc.invalidateQueries({ queryKey: listKey });
    },
    onError: (err, _vars, context) => {
      if (context?.previousList) qc.setQueryData(listKey, context.previousList);
      if (context?.previousPrio) qc.setQueryData(prioKey, context.previousPrio);
      toast.error(apiErrorDetail(err) ?? i18n.t("actionPlans.errors.saveFailed"));
    },
  });
}

export function useClearConsultationResponses(planId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (consultationId: string) =>
      unwrap(
        api.POST("/api/v1/action-plans/consultations/{id}/clear-responses/", {
          params: { path: { id: consultationId } },
          body: {} as never,
        }),
      ),
    onSuccess: () => {
      invalidatePrioritization(qc, planId);
      void qc.invalidateQueries({ queryKey: actionPlanKeys.consultations(planId) });
    },
    onError: (err) =>
      toast.error(apiErrorDetail(err) ?? i18n.t("actionPlans.errors.saveFailed")),
  });
}

export interface ConsultationEmail {
  html: string;
  recipients: string[];
  survey_url: string;
}

/** The group's saved consultation-email template + recipients (per language). */
export function useConsultationEmail(consultationId: string, enabled = true) {
  return useQuery({
    queryKey: ["consultation-email", consultationId],
    enabled: Boolean(consultationId) && enabled,
    queryFn: () =>
      unwrap(
        api.GET(
          "/api/v1/action-plans/consultations/{id}/email-template/",
          { params: { path: { id: consultationId } } },
        ),
      ) as unknown as Promise<ConsultationEmail>,
  });
}

/** Persist the consultation email template + recipients. */
export function useSaveConsultationEmail(consultationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { html: string; recipients: string }) =>
      unwrap(
        api.POST(
          "/api/v1/action-plans/consultations/{id}/email-template/",
          { params: { path: { id: consultationId } }, body: body as never },
        ),
      ) as unknown as Promise<ConsultationEmail>,
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: ["consultation-email", consultationId] }),
  });
}

/** Save-then-send the consultation email (recipients BCC'd). */
export function useSendConsultationEmail(consultationId: string) {
  return useMutation({
    mutationFn: (body: { html: string; recipients: string }) =>
      unwrap(
        api.POST(
          "/api/v1/action-plans/consultations/{id}/send-email/",
          { params: { path: { id: consultationId } }, body: body as never },
        ),
      ) as unknown as Promise<{ sent_count: number; recipients: string[] }>,
  });
}

/**
 * Download an action plan export (PDF or Excel) as a JWT-authenticated blob —
 * these endpoints return binary, not JSON, so they bypass the typed client.
 * Mirrors the reports CSV download: bearer token + refresh-on-401 retry.
 */
/** One plan's change history for the drawer.
 *
 * The endpoint is an untyped `@action` on the viewset, so this goes through
 * `fetch` rather than the generated client — the same shape as `useToolHistory`.
 * `Accept-Language` decides the field labels the server renders, so the language
 * is part of the query key: switching it must refetch, not serve a stale locale.
 * The on-behalf param is what lets a partner read a member's plan history.
 */
export function useActionPlanHistory(planId: string | null) {
  return useQuery({
    queryKey: [...actionPlanKeys.history(planId ?? ""), i18n.language],
    enabled: Boolean(planId),
    queryFn: async (): Promise<HistoryResponse> => {
      const onBehalfOf = getOnBehalfOf();
      const url =
        `${API_ORIGIN}/api/v1/action-plans/${planId}/history/` +
        (onBehalfOf ? `?on_behalf_of=${onBehalfOf}` : "");
      const res = await fetch(url, {
        headers: {
          ...(getAccessToken()
            ? { Authorization: `Bearer ${getAccessToken()}` }
            : {}),
          "Accept-Language": i18n.language,
        },
      });
      if (!res.ok) throw new Error("HTTP " + res.status);
      return (await res.json()) as HistoryResponse;
    },
  });
}

export async function downloadPlanExport(
  planId: string,
  format: "pdf" | "excel",
  filename: string,
): Promise<void> {
  const onBehalfOf = getOnBehalfOf();
  const url =
    `${API_ORIGIN}/api/v1/action-plans/${planId}/export/${format}/` +
    (onBehalfOf ? `?on_behalf_of=${onBehalfOf}` : "");
  const request = (token: string | null) =>
    fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });

  let response = await request(getAccessToken());
  if (response.status === 401) {
    const refreshed = await refreshAccessToken();
    if (refreshed) response = await request(refreshed);
  }
  if (!response.ok) {
    throw new Error(`Export failed (${response.status})`);
  }
  const blob = await response.blob();
  const href = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(href);
}
